import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Mic, Square, Image, ChevronDown, ChevronUp } from 'lucide-react';

const ACTIVITIES = {
  A1: [
    {
      id: 'avion-voyage',
      title: "Dans l'avion",
      objectif: "Décrire une scène dans un avion. Apprendre le vocabulaire du voyage.",
      instructions: "Regardez bien l'image. Décrivez ce que vous voyez avec des phrases simples.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/93e9idqw_Airoplane%20A1.png',
      observation: [
        "Qu'est-ce que vous voyez dans l'image ?",
        "Où sont les personnes ?",
        "Que fait la femme au premier plan ?",
      ],
      vocabulaire: [
        { mot: "l'avion", def: 'le moyen de transport qui vole' },
        { mot: 'un passager', def: 'une personne dans le transport' },
        { mot: 'un siège', def: 'la place pour s\'asseoir' },
        { mot: 'le hublot', def: 'la petite fenêtre de l\'avion' },
        { mot: "l'hôtesse de l'air", def: 'la personne qui aide les passagers' },
        { mot: 'un écran', def: 'pour regarder des films' },
        { mot: 'le ciel', def: 'ce qu\'on voit par le hublot' },
      ],
      expressionGuidee: [
        "Il y a beaucoup de passagers dans l'avion.",
        "Je vois une femme qui regarde par le hublot.",
        "C'est l'intérieur d'un avion.",
        "Il y a des écrans sur les sièges.",
        "Je vois une hôtesse de l'air dans l'allée.",
      ],
      expressionOrale: "Décrivez l'image pendant 30 à 60 secondes. Dites ce que vous voyez, qui sont les personnes et ce qu'elles font.",
      exemple: [
        "C'est l'intérieur d'un avion. Il y a beaucoup de passagers.",
        "Une femme est assise près du hublot. Elle regarde le ciel.",
        "Il y a une hôtesse de l'air qui marche dans l'allée.",
        "Les sièges ont des écrans. Les passagers voyagent.",
      ],
    },
    {
      id: 'plage-maman',
      title: 'Une journée à la plage avec maman',
      objectif: 'Décrire une scène de vacances à la plage. Apprendre le vocabulaire de la mer et des jeux.',
      instructions: "Observez l'image attentivement. Utilisez des mots simples pour décrire la scène.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/ksubaz81_Beach%20day%20with%20mom.png',
      observation: [
        "Qui voyez-vous dans l'image ?",
        'Où sont-ils ?',
        "Qu'est-ce que l'enfant fait ?",
      ],
      vocabulaire: [
        { mot: 'la plage', def: 'le bord de la mer avec du sable' },
        { mot: 'le sable', def: 'ce qui est sur la plage (jaune)' },
        { mot: 'un château de sable', def: 'une construction avec du sable' },
        { mot: 'un parasol', def: 'pour se protéger du soleil' },
        { mot: 'le soleil', def: 'il fait chaud et il brille' },
        { mot: 'un cerf-volant', def: 'un jouet qui vole dans le ciel' },
        { mot: 'un seau', def: 'un petit récipient pour le sable' },
        { mot: 'un chapeau', def: 'on le met sur la tête' },
      ],
      expressionGuidee: [
        'Il y a un enfant et sa maman sur la plage.',
        "Je vois un château de sable. L'enfant le construit.",
        "C'est une belle journée, il y a du soleil.",
        'Il y a un cerf-volant dans le ciel.',
        'Je vois un parasol orange et jaune.',
      ],
      expressionOrale: "Parlez de cette image pendant 30 à 60 secondes. Décrivez les personnes, le lieu et les activités.",
      exemple: [
        'Une maman et son fils sont à la plage. Il fait beau.',
        "L'enfant construit un château de sable. Il est content.",
        'La maman porte un chapeau et tient un parasol.',
        'Il y a un cerf-volant dans le ciel et un seau rouge.',
      ],
    },
    {
      id: 'plage-chien',
      title: 'Courir sur la plage avec son chien',
      objectif: 'Décrire une activité en plein air. Apprendre les verbes de mouvement.',
      instructions: "Regardez l'image. Décrivez l'action et les personnages.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/ewf5h02g_beach%20day.png',
      observation: [
        "Qui est dans l'image ?",
        "Que font-ils ?",
        "Comment est le temps ?",
      ],
      vocabulaire: [
        { mot: 'courir', def: 'aller très vite avec les jambes' },
        { mot: 'un chien', def: "l'animal domestique" },
        { mot: 'la mer', def: "l'eau bleue, grande" },
        { mot: 'le sable', def: 'sur la plage, de couleur jaune' },
        { mot: 'une fille', def: 'une jeune personne' },
        { mot: 'content / contente', def: 'heureux, joyeux' },
        { mot: 'les nuages', def: 'dans le ciel, blancs' },
      ],
      expressionGuidee: [
        'Je vois une fille et un chien sur la plage.',
        'Il y a la mer derrière eux.',
        "C'est une journée avec du soleil et des nuages.",
        'La fille court avec son chien. Elle est contente.',
        'Il y a du sable jaune sur la plage.',
      ],
      expressionOrale: "Décrivez cette scène pendant 30 à 60 secondes. Utilisez les verbes courir, jouer et voir.",
      exemple: [
        'Une fille court sur la plage avec son chien.',
        'Elle porte un t-shirt jaune. Elle est contente.',
        'Le chien est marron. Il court à côté de la fille.',
        'Il fait beau. Il y a la mer et des nuages blancs.',
      ],
    },
    {
      id: 'cafe-restaurant',
      title: 'Au restaurant',
      objectif: 'Décrire une scène au restaurant. Apprendre le vocabulaire de la restauration.',
      instructions: "Observez l'image. Décrivez les personnes, le lieu et la situation.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/le6l83rp_Cafe%20order.png',
      observation: [
        "Où sont les personnes ?",
        "Combien de personnes voyez-vous ?",
        "Que font les personnes assises ?",
      ],
      vocabulaire: [
        { mot: 'le restaurant', def: 'un lieu pour manger' },
        { mot: 'le serveur', def: 'la personne qui sert les clients' },
        { mot: 'un menu / la carte', def: 'la liste des plats' },
        { mot: 'une table', def: 'le meuble pour manger' },
        { mot: 'un verre', def: 'pour boire' },
        { mot: 'commander', def: 'choisir ce qu\'on veut manger' },
        { mot: 'un client', def: 'la personne qui mange au restaurant' },
      ],
      expressionGuidee: [
        'Je vois deux personnes assises à une table.',
        'Il y a un serveur debout. Il porte un tablier.',
        "C'est un restaurant. Il y a des lumières.",
        'Les clients lisent le menu. Ils vont commander.',
        'Il y a des verres sur la table.',
      ],
      expressionOrale: "Décrivez cette scène au restaurant pendant 30 à 60 secondes. Dites qui sont les personnes et ce qu'elles font.",
      exemple: [
        'Deux personnes sont assises dans un restaurant. Ils lisent le menu.',
        'Un serveur est debout à côté de la table. Il attend la commande.',
        'Il y a des verres sur la table. Le restaurant est joli.',
        "C'est le soir. Les clients vont choisir leur repas.",
      ],
    },
    {
      id: 'velo-nature',
      title: 'Faire du vélo dans la nature',
      objectif: 'Décrire un paysage et une activité sportive. Apprendre le vocabulaire de la nature.',
      instructions: "Regardez l'image avec attention. Décrivez le paysage et l'activité.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/38dncnkp_cheerful%20kid%20biking.png',
      observation: [
        "Que fait l'enfant ?",
        "Comment est le paysage ?",
        "Quels éléments de la nature voyez-vous ?",
      ],
      vocabulaire: [
        { mot: 'un vélo', def: 'un moyen de transport à deux roues' },
        { mot: 'la nature', def: 'les arbres, les fleurs, les montagnes' },
        { mot: 'les fleurs', def: 'elles sont colorées, dans la nature' },
        { mot: 'une montagne', def: 'très haute, avec de la terre et des roches' },
        { mot: 'une rivière', def: "de l'eau qui coule" },
        { mot: 'les oiseaux', def: 'les animaux qui volent dans le ciel' },
        { mot: 'un chemin', def: 'une petite route dans la nature' },
        { mot: 'le ciel', def: 'il est bleu avec des nuages' },
      ],
      expressionGuidee: [
        'Je vois un enfant qui fait du vélo.',
        'Il y a des fleurs de toutes les couleurs.',
        "C'est un paysage magnifique avec des montagnes.",
        'Il y a une rivière bleue et des oiseaux dans le ciel.',
        "L'enfant est content. Il roule sur un chemin.",
      ],
      expressionOrale: "Décrivez cette image pendant 30 à 60 secondes. Parlez du paysage, de l'enfant et de la nature.",
      exemple: [
        "Un enfant fait du vélo sur un chemin dans la nature. Il est content.",
        'Il y a beaucoup de fleurs rouges, oranges et blanches.',
        'Derrière, il y a des montagnes vertes et une rivière bleue.',
        'Des oiseaux volent dans le ciel. Le ciel est bleu avec des nuages.',
      ],
    },
  ],
  A2: [
    {
      id: 'rentree-ecole',
      title: "Le premier jour d'école",
      objectif: "Décrire une scène de rentrée scolaire. Exprimer des émotions et donner son opinion.",
      instructions: "Regardez l'image attentivement. Répondez aux questions en faisant des phrases complètes. Utilisez parce que pour justifier vos réponses.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/2z5kby57_1st%20day%20of%20school.png',
      observation: [
        "Que voyez-vous dans cette image ?",
        "Qui sont les personnes ?",
        "Où sont-ils ?",
      ],
      actions: [
        "Que font les parents ?",
        "Que fait l'enfant ?",
      ],
      situation: "Que se passe-t-il dans cette scène ? Décrivez la situation.",
      pourquoi: "Pourquoi les parents accompagnent-ils leur enfant ? Répondez avec parce que.",
      expressionPersonnelle: [
        "Est-ce que vous vous souvenez de votre premier jour d'école ?",
        "Est-ce que vous étiez content(e) ou nerveux/nerveuse ?",
      ],
      vocabulaire: [
        { mot: "l'école", def: "le lieu où les enfants apprennent" },
        { mot: 'un sac à dos', def: 'on le porte sur le dos pour transporter les affaires' },
        { mot: 'la rentrée', def: "le premier jour d'école après les vacances" },
        { mot: 'accompagner', def: "aller avec quelqu'un" },
        { mot: 'nerveux / nerveuse', def: "un peu stressé, inquiet" },
        { mot: 'les parents', def: "le papa et la maman" },
        { mot: 'encourager', def: 'donner de la confiance à quelqu\'un' },
      ],
      expressionOrale: "Parlez de cette image pendant 2 à 3 minutes. Décrivez la scène, les personnes, leurs émotions et donnez votre avis.",
      exemple: [
        "Dans cette image, je vois un enfant avec ses parents devant une école.",
        "C'est probablement le premier jour d'école. L'enfant porte un sac à dos.",
        "La maman ajuste les vêtements de l'enfant. Elle l'encourage.",
        "Le papa est derrière. Il semble fier de son fils.",
        "D'autres enfants entrent dans l'école en arrière-plan.",
      ],
    },
    {
      id: 'campus-universitaire',
      title: 'Sur le campus universitaire',
      objectif: "Décrire une scène de vie étudiante. Parler de l'amitié et des études.",
      instructions: "Observez l'image. Décrivez les personnes, le lieu et la situation. Donnez votre opinion.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/nnbbl4lr_A2%20college%20campus.png',
      observation: [
        "Que voyez-vous dans l'image ?",
        "Qui sont les personnes ?",
        "Où sont-elles ?",
      ],
      actions: [
        "Que font les deux personnes au premier plan ?",
        "Que font les personnes en arrière-plan ?",
      ],
      situation: "Que se passe-t-il ? Décrivez la relation entre les deux étudiants.",
      pourquoi: "Pourquoi les étudiants aiment-ils étudier dehors ? Répondez avec parce que.",
      expressionPersonnelle: [
        "Est-ce que vous aimez étudier dehors ?",
        "Est-ce que vous étudiez seul(e) ou avec des amis ?",
      ],
      vocabulaire: [
        { mot: 'un campus', def: "le terrain de l'université" },
        { mot: 'un étudiant / une étudiante', def: 'une personne qui étudie à l\'université' },
        { mot: 'un téléphone portable', def: 'un smartphone' },
        { mot: 'la pelouse', def: "l'herbe verte dans un parc" },
        { mot: 'discuter', def: 'parler ensemble' },
        { mot: 'un vélo', def: 'moyen de transport à deux roues' },
        { mot: 'réviser', def: 'étudier pour un examen' },
        { mot: 'un livre', def: 'on le lit pour apprendre' },
      ],
      expressionOrale: "Décrivez cette image pendant 2 à 3 minutes. Parlez des étudiants, du campus et de la vie universitaire.",
      exemple: [
        "Deux étudiants sont assis sur la pelouse d'un campus universitaire.",
        "Le garçon montre quelque chose sur son téléphone à la fille.",
        "La fille a un livre ouvert. Ils discutent ensemble.",
        "En arrière-plan, il y a d'autres étudiants et des vélos.",
        "C'est une belle journée. Ils aiment étudier dehors parce que c'est agréable.",
      ],
    },
    {
      id: 'bureau-inscription',
      title: "Au bureau des inscriptions",
      objectif: "Décrire une situation administrative. Pratiquer le vocabulaire des formalités.",
      instructions: "Observez bien la scène. Décrivez la situation, les personnes et leurs actions.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/j94y7d8l_Admissions%20office%20A2.png',
      observation: [
        "Que voyez-vous dans l'image ?",
        "Qui sont les personnes ?",
        "Où se passe la scène ?",
      ],
      actions: [
        "Que fait l'étudiant au comptoir ?",
        "Que fait la femme derrière le bureau ?",
      ],
      situation: "Que se passe-t-il ? Pourquoi l'étudiant est-il là ?",
      pourquoi: "Pourquoi doit-on s'inscrire à l'université ? Répondez avec parce que.",
      expressionPersonnelle: [
        "Est-ce que vous avez déjà fait une inscription administrative ?",
        "Est-ce que les formalités administratives sont faciles dans votre pays ?",
      ],
      vocabulaire: [
        { mot: "l'inscription", def: "s'enregistrer officiellement" },
        { mot: 'un formulaire', def: 'un document à remplir' },
        { mot: 'un comptoir', def: 'le bureau où on parle avec un employé' },
        { mot: 'un dossier', def: 'un ensemble de documents' },
        { mot: "une pièce d'identité", def: 'un document qui prouve qui tu es' },
        { mot: "faire la queue", def: 'attendre son tour' },
        { mot: 'un ordinateur', def: 'un outil de travail avec un écran' },
      ],
      expressionOrale: "Décrivez cette scène pendant 2 à 3 minutes. Parlez des personnes, de la situation et des démarches administratives.",
      exemple: [
        "Un jeune homme est au bureau des inscriptions. Il parle avec une employée.",
        "L'employée est assise devant un ordinateur. Elle regarde des documents.",
        "Derrière l'étudiant, d'autres personnes font la queue et attendent leur tour.",
        "L'étudiant a son sac à dos. Il est probablement nouveau à l'université.",
        "Il doit remplir un formulaire et donner ses documents pour s'inscrire.",
      ],
    },
    {
      id: 'fete-anniversaire',
      title: "La fête d'anniversaire",
      objectif: "Décrire une fête. Parler des célébrations et exprimer la joie.",
      instructions: "Regardez l'image. Décrivez la fête, les personnes et les émotions. Donnez votre avis personnel.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/gmmsxa4h_birthday%20party.png',
      observation: [
        "Que voyez-vous dans l'image ?",
        "Combien de personnes sont présentes ?",
        "Où se passe la scène ?",
      ],
      actions: [
        "Que fait l'enfant au centre ?",
        "Que font les autres enfants autour de lui ?",
      ],
      situation: "Que se passe-t-il ? Quel événement est célébré ?",
      pourquoi: "Pourquoi les enfants sont-ils contents ? Répondez avec parce que.",
      expressionPersonnelle: [
        "Est-ce que vous aimez les fêtes d'anniversaire ?",
        "Comment célébrez-vous votre anniversaire ?",
      ],
      vocabulaire: [
        { mot: "un anniversaire", def: "le jour où on célèbre sa naissance" },
        { mot: 'un gâteau', def: "on le mange pour célébrer" },
        { mot: 'des bougies', def: 'on les met sur le gâteau' },
        { mot: 'souffler', def: 'pousser de l\'air avec la bouche' },
        { mot: 'un cadeau', def: "quelque chose qu'on offre" },
        { mot: 'des ballons', def: 'ils sont ronds et colorés' },
        { mot: 'un chapeau de fête', def: 'un chapeau pointu pour la fête' },
        { mot: 'applaudir', def: 'frapper ses mains ensemble' },
      ],
      expressionOrale: "Décrivez cette fête pendant 2 à 3 minutes. Parlez des personnes, de l'ambiance et de vos propres souvenirs d'anniversaire.",
      exemple: [
        "C'est une fête d'anniversaire. Un enfant souffle les bougies sur un gâteau.",
        "Autour de lui, ses amis applaudissent et portent des chapeaux de fête.",
        "Il y a des ballons colorés et des cadeaux sur la table.",
        "Les enfants sont très contents parce que c'est un moment spécial.",
        "La décoration est jolie avec des guirlandes et des couleurs vives.",
      ],
    },
    {
      id: 'arret-de-bus',
      title: "À l'arrêt de bus",
      objectif: "Décrire une scène urbaine. Parler des transports en commun et de la vie quotidienne.",
      instructions: "Observez cette scène de rue. Décrivez le lieu, les personnes et la situation.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/y2xv0ifb_Bus%20stop%20A2.png',
      observation: [
        "Que voyez-vous dans l'image ?",
        "Qui sont les personnes ?",
        "Où se trouvent-elles ?",
      ],
      actions: [
        "Que fait le jeune homme qui regarde le panneau ?",
        "Que fait l'autre personne ?",
      ],
      situation: "Que se passe-t-il ? Pourquoi sont-ils là ?",
      pourquoi: "Pourquoi les gens utilisent-ils le bus ? Répondez avec parce que.",
      expressionPersonnelle: [
        "Est-ce que vous utilisez les transports en commun ?",
        "Préférez-vous le bus, le métro ou la voiture ? Pourquoi ?",
      ],
      vocabulaire: [
        { mot: "un arrêt de bus", def: "le lieu où on attend le bus" },
        { mot: 'un panneau', def: 'un écran qui montre les horaires' },
        { mot: 'un bus', def: 'un grand véhicule de transport' },
        { mot: 'attendre', def: 'rester en place pour quelque chose' },
        { mot: 'un horaire', def: "l'heure de passage du bus" },
        { mot: 'un trajet', def: "le chemin entre deux points" },
        { mot: 'la rue', def: "le chemin en ville avec des voitures" },
        { mot: 'un sac à dos', def: 'on le porte sur le dos' },
      ],
      expressionOrale: "Décrivez cette scène pendant 2 à 3 minutes. Parlez du lieu, des personnes et de votre expérience avec les transports.",
      exemple: [
        "Deux jeunes hommes sont à un arrêt de bus en ville. Il fait froid.",
        "Un garçon regarde le panneau d'horaires. Le bus numéro 10 arrive dans 6 minutes.",
        "L'autre personne regarde son téléphone en attendant.",
        "Un bus arrive au loin dans la rue. La ville est décorée avec des lumières.",
        "Ils prennent probablement le bus parce que c'est pratique et pas cher.",
      ],
    },
  ],
  B1: [
    {
      id: 'addiction-telephone',
      title: "L'addiction au téléphone",
      objectif: "Interpréter une situation sociale, exprimer une opinion argumentée sur l'usage des technologies.",
      instructions: "Analysez cette image en détail. Décrivez la situation, interprétez-la et donnez votre opinion personnelle avec des justifications.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/avucet2n_aloof%20addicted%20to%20phone.png',
      analyse: [
        "Que se passe-t-il dans cette image ?",
        "Quelle est la situation générale ? Décrivez l'atmosphère.",
        "Quels sont les problèmes visibles dans cette scène ?",
      ],
      interpretation: [
        "Pourquoi le jeune homme est-il isolé malgré la présence d'autres personnes ?",
        "Quelles peuvent être les conséquences de ce comportement à long terme ?",
      ],
      opinion: [
        "Êtes-vous d'accord avec ce comportement ? Pourquoi ?",
        "Que pensez-vous de l'utilisation excessive du téléphone en société ?",
      ],
      solution: [
        "Que peut-on faire pour réduire la dépendance au téléphone ?",
        "Proposez une ou deux solutions concrètes.",
      ],
      comparaison: "Est-ce que cette situation est courante dans votre pays ? Les jeunes sont-ils souvent sur leur téléphone en public ?",
      vocabulaire: [
        { mot: "l'addiction", def: "une dépendance difficile à contrôler" },
        { mot: "s'isoler", def: "se mettre à l'écart des autres" },
        { mot: 'les réseaux sociaux', def: 'Instagram, TikTok, Facebook…' },
        { mot: 'ignorer', def: 'ne pas faire attention à quelqu\'un' },
        { mot: 'une notification', def: 'un message qui apparaît sur le téléphone' },
        { mot: 'la solitude', def: 'le fait d\'être seul' },
        { mot: 'le lien social', def: 'la connexion entre les personnes' },
      ],
      expressionOrale: "Parlez de cette image pendant 2 à 3 minutes. Analysez la situation, donnez votre opinion et proposez des solutions.",
      exemple: [
        "Dans cette image, je vois un jeune homme assis sur un banc dans un parc. Il regarde son téléphone avec beaucoup d'attention.",
        "Derrière lui, trois personnes discutent ensemble, mais il ne participe pas à la conversation.",
        "Je pense que cette situation illustre l'addiction au téléphone, parce que le garçon préfère regarder son écran plutôt que parler avec les autres.",
        "À mon avis, c'est un problème de société important, car les réseaux sociaux créent une illusion de connexion tout en isolant les gens dans la vie réelle.",
        "Il faut encourager les moments sans téléphone, par exemple pendant les repas ou les sorties entre amis.",
        "On doit aussi sensibiliser les jeunes aux dangers de l'addiction numérique.",
      ],
    },
    {
      id: 'gare-occupee',
      title: 'La gare bondée — Retards et frustration',
      objectif: "Décrire une situation de stress collectif. Analyser les causes et conséquences des problèmes de transport.",
      instructions: "Observez attentivement cette scène. Analysez la situation, interprétez les émotions des personnes et proposez des solutions.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/bq9muapo_busy%20station.png',
      analyse: [
        "Que se passe-t-il dans cette gare ?",
        "Quelle est la situation générale ? Que montrent les panneaux ?",
        "Quels problèmes voyez-vous dans cette image ?",
      ],
      interpretation: [
        "Pourquoi y a-t-il autant de monde sur le quai ?",
        "Quelles sont les conséquences d'un retard de train pour les voyageurs ?",
      ],
      opinion: [
        "Que pensez-vous de la qualité des transports en commun dans les grandes villes ?",
        "Êtes-vous souvent confronté(e) à ce type de situation ?",
      ],
      solution: [
        "Comment les compagnies de transport pourraient-elles améliorer le service ?",
        "Que peut-on faire en tant que voyageur quand un train est en retard ?",
      ],
      comparaison: "Est-ce que les transports en commun fonctionnent bien dans votre pays ? Avez-vous des problèmes similaires ?",
      vocabulaire: [
        { mot: 'un retard', def: 'quand le train ne part pas à l\'heure' },
        { mot: 'un quai', def: 'la plateforme où on attend le train' },
        { mot: 'bondé(e)', def: 'plein de monde, surchargé' },
        { mot: 'la frustration', def: 'un sentiment négatif quand quelque chose ne va pas' },
        { mot: 'un panneau d\'affichage', def: 'un écran qui montre les horaires' },
        { mot: 'un mégaphone', def: 'un appareil pour parler fort à une foule' },
        { mot: 'les transports en commun', def: 'bus, train, métro…' },
        { mot: 'une grève', def: 'quand les employés arrêtent de travailler pour protester' },
      ],
      expressionOrale: "Décrivez cette scène pendant 2 à 3 minutes. Analysez le problème, expliquez les conséquences et proposez des solutions.",
      exemple: [
        "Dans cette image, je vois une gare très bondée. Il y a énormément de monde sur le quai.",
        "Les panneaux d'affichage indiquent que plusieurs trains sont en retard — « Delayed ». Un homme utilise un mégaphone pour communiquer avec les voyageurs.",
        "Je pense que cette situation est très stressante pour les passagers, parce qu'ils ont probablement des rendez-vous importants ou des correspondances à prendre.",
        "À mon avis, les compagnies de transport devraient investir davantage dans la maintenance des trains pour éviter ces retards.",
        "On doit aussi améliorer la communication avec les voyageurs en temps réel, par exemple avec des applications mobiles.",
        "Il faut que les gouvernements investissent dans les infrastructures de transport pour éviter ce type de situation.",
      ],
    },
    {
      id: 'colocataires',
      title: 'La vie en colocation',
      objectif: "Analyser une situation de vie quotidienne partagée. Discuter des avantages et des défis de la colocation.",
      instructions: "Regardez cette image avec attention. Décrivez ce que chaque personne fait, interprétez la dynamique du groupe et donnez votre opinion.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/f2n0btfl_colocataires.png',
      analyse: [
        "Que se passe-t-il dans cette image ? Décrivez chaque personne.",
        "Quelle est la situation générale dans cet appartement ?",
        "Quels sont les problèmes potentiels de cette scène ?",
      ],
      interpretation: [
        "Pourquoi chaque colocataire fait-elle une activité différente ?",
        "Comment est-ce que le partage des tâches peut devenir un problème ?",
      ],
      opinion: [
        "Que pensez-vous de la colocation ? Est-ce une bonne idée ?",
        "Préférez-vous vivre seul(e) ou en colocation ? Pourquoi ?",
      ],
      solution: [
        "Comment peut-on organiser la vie en colocation pour éviter les conflits ?",
        "Proposez des règles pour une bonne colocation.",
      ],
      comparaison: "Est-ce que la colocation est courante dans votre pays ? Les étudiants vivent-ils souvent ensemble ?",
      vocabulaire: [
        { mot: 'une colocation', def: 'vivre à plusieurs dans un même logement' },
        { mot: 'un(e) colocataire', def: 'une personne avec qui on partage le logement' },
        { mot: 'le partage des tâches', def: 'répartir le ménage, la cuisine…' },
        { mot: 'le ménage', def: 'nettoyer, ranger la maison' },
        { mot: 'un balai', def: 'un outil pour nettoyer le sol' },
        { mot: 'un conflit', def: 'un désaccord entre personnes' },
        { mot: 'le respect', def: 'considérer les besoins des autres' },
        { mot: 'les règles de vie', def: 'les accords pour vivre ensemble' },
      ],
      expressionOrale: "Parlez de cette image pendant 2 à 3 minutes. Décrivez la situation, analysez la dynamique de groupe et donnez votre avis sur la colocation.",
      exemple: [
        "Dans cette image, je vois trois colocataires dans un appartement partagé. Chacune fait une activité différente.",
        "Une fille cuisine dans la cuisine, une autre est assise sur le canapé et étudie avec des livres, et la troisième fait le ménage avec un balai et des écouteurs.",
        "Je pense que cette scène montre bien la réalité de la colocation, parce que chaque personne a ses propres responsabilités et son propre rythme.",
        "À mon avis, la colocation est une bonne idée quand on est étudiant, car cela permet de partager les frais. Cependant, il faut établir des règles claires.",
        "Il faut organiser un planning pour le ménage et la cuisine, sinon les conflits peuvent vite apparaître.",
        "On doit aussi respecter le besoin de calme de chacun, surtout pendant les périodes d'examens.",
      ],
    },
    {
      id: 'cuisine-famille',
      title: 'Cuisiner en famille',
      objectif: "Interpréter une scène familiale positive. Discuter de l'importance des moments partagés et de la transmission.",
      instructions: "Analysez cette scène de cuisine familiale. Décrivez les actions, interprétez la dynamique familiale et exprimez votre opinion.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/8r4huk10_family%20cooking%20time.png',
      analyse: [
        "Que se passe-t-il dans cette image ?",
        "Qui sont les personnes et que fait chacune d'elles ?",
        "Quelle est l'atmosphère de cette scène ?",
      ],
      interpretation: [
        "Pourquoi est-il important de cuisiner ensemble en famille ?",
        "Quels sont les bénéfices éducatifs pour l'enfant dans cette situation ?",
      ],
      opinion: [
        "Que pensez-vous des activités en famille comme la cuisine ?",
        "Est-ce que vous cuisinez souvent avec votre famille ?",
      ],
      solution: [
        "Comment encourager les familles à passer plus de temps ensemble ?",
        "Que peut-on faire pour réduire l'influence des écrans pendant les repas ?",
      ],
      comparaison: "Dans votre culture, est-ce que les familles cuisinent souvent ensemble ? Comment se passent les repas chez vous ?",
      vocabulaire: [
        { mot: 'un tablier', def: 'un vêtement de protection pour cuisiner' },
        { mot: 'un saladier', def: 'un grand bol pour mélanger' },
        { mot: 'une recette', def: 'les instructions pour préparer un plat' },
        { mot: 'transmettre', def: 'apprendre quelque chose à quelqu\'un' },
        { mot: 'un ingrédient', def: 'un aliment utilisé dans une recette' },
        { mot: 'mélanger', def: 'remuer les ingrédients ensemble' },
        { mot: 'une casserole', def: 'un récipient pour cuire sur le feu' },
        { mot: 'le lien familial', def: 'la connexion entre les membres d\'une famille' },
      ],
      expressionOrale: "Parlez de cette image pendant 2 à 3 minutes. Décrivez la scène, analysez l'importance de ce moment et partagez vos expériences personnelles.",
      exemple: [
        "Dans cette image, je vois une famille qui cuisine ensemble dans la cuisine. Il y a le père, la mère et leur fils.",
        "Le père aide son fils à mélanger les ingrédients dans un saladier. La mère est à côté et prépare autre chose sur la cuisinière.",
        "Je pense que cuisiner en famille est très important, parce que cela permet de passer du temps ensemble et de transmettre des traditions.",
        "À mon avis, les enfants apprennent beaucoup en cuisinant avec leurs parents : la patience, la créativité et le goût des bonnes choses.",
        "Il faut encourager ces moments, surtout aujourd'hui où les écrans prennent beaucoup de place dans la vie quotidienne.",
        "On doit profiter de ces instants simples pour renforcer les liens familiaux.",
      ],
    },
    {
      id: 'travail-groupe',
      title: 'Le travail en groupe à la bibliothèque',
      objectif: "Analyser une situation d'apprentissage collectif. Discuter des avantages et défis du travail en groupe.",
      instructions: "Observez cette scène d'étude en groupe. Décrivez les interactions, interprétez la dynamique et donnez votre avis sur le travail collaboratif.",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/zvar7iz9_group%20study.png',
      analyse: [
        "Que se passe-t-il dans cette image ?",
        "Quelle est la situation générale ? Où sont les étudiants ?",
        "Quels outils utilisent-ils pour travailler ?",
      ],
      interpretation: [
        "Pourquoi ces étudiants ont-ils choisi de travailler ensemble ?",
        "Quelles difficultés peuvent apparaître pendant un travail de groupe ?",
      ],
      opinion: [
        "Préférez-vous travailler seul(e) ou en groupe ? Pourquoi ?",
        "Que pensez-vous du travail collaboratif à l'université ?",
      ],
      solution: [
        "Comment organiser un travail de groupe efficace ?",
        "Que faire quand un membre du groupe ne participe pas ?",
      ],
      comparaison: "Comment se passe le travail de groupe dans votre pays ? Les étudiants travaillent-ils souvent ensemble ?",
      vocabulaire: [
        { mot: 'un exposé', def: 'une présentation orale devant la classe' },
        { mot: 'collaborer', def: 'travailler ensemble vers un objectif commun' },
        { mot: 'un ordinateur portable', def: 'un laptop' },
        { mot: 'des notes', def: 'ce qu\'on écrit pendant un cours' },
        { mot: 'débattre', def: 'discuter en donnant des arguments' },
        { mot: 'la répartition', def: 'diviser le travail entre les personnes' },
        { mot: 'un délai', def: 'la date limite pour rendre un travail' },
        { mot: 'la concentration', def: 'l\'attention focalisée sur une tâche' },
      ],
      expressionOrale: "Décrivez cette scène pendant 2 à 3 minutes. Analysez les avantages du travail en groupe, les défis possibles et partagez votre expérience.",
      exemple: [
        "Dans cette image, je vois quatre étudiants qui travaillent ensemble autour d'une table, probablement dans une bibliothèque.",
        "Ils ont des ordinateurs portables, des livres et des feuilles de notes. Un garçon semble expliquer quelque chose aux autres en faisant des gestes.",
        "Je pense que le travail en groupe est très utile, parce qu'il permet d'échanger des idées et de voir les choses sous différents angles.",
        "Cependant, à mon avis, il peut aussi être difficile si les membres du groupe n'ont pas le même niveau de motivation.",
        "Il faut bien répartir les tâches dès le début et fixer des objectifs clairs pour que tout le monde contribue.",
        "On doit aussi apprendre à écouter les opinions des autres, même quand on n'est pas d'accord.",
      ],
    },
  ],
  B2: [
    {
      id: 'femme-travail',
      title: 'La place des femmes dans le monde professionnel',
      objectif: "Développer un exposé structuré sur l'égalité professionnelle, argumenter et nuancer.",
      instructions: "Préparez un exposé oral de 3 à 5 minutes. Organisez votre réponse (introduction, développement, conclusion).",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/d20get2g_woman%20at%20work.png',
      analyse: [
        "Décrivez brièvement la situation représentée dans cette image.",
        "Quel est le thème principal de cette image ?",
        "Quels enjeux sociaux et professionnels peut-on identifier ?",
      ],
      problematique: "Dans quelle mesure l'accès des femmes aux postes de responsabilité a-t-il évolué, et quels obstacles persistent encore aujourd'hui ?",
      argumentation: [
        "Donnez au moins deux arguments en faveur de la parité en entreprise. Illustrez avec des exemples concrets.",
        "Comment les politiques de quotas ou de mentorat peuvent-elles contribuer à l'égalité professionnelle ?",
        "Développez l'idée selon laquelle la diversité au sein des équipes améliore la performance collective.",
      ],
      nuance: [
        "Quels sont les avantages et les inconvénients des politiques de discrimination positive ?",
        "Existe-t-il des limites ou des critiques à l'approche actuelle de l'égalité professionnelle ?",
      ],
      opinion: [
        "Quelle est votre position sur la place des femmes dans les postes de direction ?",
        "Êtes-vous favorable aux quotas de genre en entreprise ? Pourquoi ?",
      ],
      ouverture: [
        "Comment voyez-vous l'évolution de l'égalité professionnelle dans les 20 prochaines années ?",
        "Quelles mesures concrètes les entreprises et les gouvernements pourraient-ils mettre en place ?",
      ],
      vocabulaire: [
        { mot: 'la parité', def: "l'égalité de représentation entre hommes et femmes" },
        { mot: 'le plafond de verre', def: "les barrières invisibles qui empêchent les femmes d'accéder aux postes élevés" },
        { mot: 'un poste de direction', def: 'un emploi à haute responsabilité dans une entreprise' },
        { mot: "la discrimination positive", def: "des mesures favorisant les groupes sous-représentés" },
        { mot: 'le leadership', def: "la capacité à diriger et à inspirer une équipe" },
        { mot: "l'écart salarial", def: "la différence de rémunération entre hommes et femmes" },
        { mot: 'le mentorat', def: "l'accompagnement professionnel par une personne expérimentée" },
        { mot: 'la légitimité', def: "le fait d'être reconnu(e) comme compétent(e) et à sa place" },
      ],
      expressionOrale: "Préparez un exposé de 3 à 5 minutes. Présentez la situation, développez vos arguments, nuancez votre propos et concluez avec une ouverture.",
      exemple: [
        "Cette image représente une femme cadre qui présente des résultats financiers à une équipe composée majoritairement d'hommes. Cette scène illustre parfaitement la question de la place des femmes dans le monde professionnel.",
        "Tout d'abord, il faut reconnaître que des progrès considérables ont été réalisés ces dernières décennies. Les femmes accèdent de plus en plus à des postes de responsabilité, notamment grâce aux politiques de parité et aux évolutions législatives.",
        "De plus, de nombreuses études montrent que la diversité de genre au sein des équipes dirigeantes améliore la performance des entreprises, car elle apporte des perspectives variées et une meilleure prise de décision.",
        "En revanche, il serait naïf de nier que des obstacles persistants existent encore. Le plafond de verre reste une réalité dans de nombreux secteurs, et l'écart salarial entre hommes et femmes n'a pas totalement disparu.",
        "Cependant, certains critiquent les quotas de genre, estimant qu'ils peuvent conduire à des recrutements basés sur le genre plutôt que sur les compétences, ce qui peut nuire à la légitimité des femmes promues.",
        "À mon avis, l'égalité professionnelle ne se décrète pas uniquement par des lois. Elle nécessite un changement culturel profond, dès l'éducation, pour déconstruire les stéréotypes de genre.",
        "Il faut également encourager le mentorat et la formation des femmes au leadership, afin de les préparer à assumer des responsabilités élevées avec confiance.",
        "En conclusion, bien que la situation ait évolué positivement, le chemin vers une véritable égalité professionnelle reste long. L'avenir dépendra de la volonté collective des entreprises, des gouvernements et de la société civile à transformer durablement les mentalités.",
      ],
    },
    {
      id: 'concept-abstrait',
      title: 'Le marché des idées — La valeur des concepts abstraits',
      objectif: "Développer un exposé structuré sur la commercialisation des idées et des émotions, argumenter et nuancer.",
      instructions: "Préparez un exposé oral de 3 à 5 minutes. Organisez votre réponse (introduction, développement, conclusion).",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/piwhcqg3_Abstract%20concept.png',
      analyse: [
        "Décrivez brièvement ce que vous voyez dans cette image fantastique.",
        "Quel est le thème principal suggéré par les panneaux et les symboles lumineux ?",
        "Quels enjeux philosophiques et commerciaux peut-on identifier dans cette représentation ?",
      ],
      problematique: "Peut-on dire que les idées et les émotions sont devenues des marchandises dans notre société contemporaine, et quelles en sont les conséquences ?",
      argumentation: [
        "Donnez au moins deux arguments montrant comment les concepts abstraits (liberté, succès, amour) sont utilisés à des fins commerciales. Illustrez avec des exemples.",
        "Comment le marketing et la publicité exploitent-ils les émotions humaines pour vendre des produits ou des idées ?",
        "Développez l'idée selon laquelle l'art et la créativité permettent de donner une valeur aux concepts immatériels.",
      ],
      nuance: [
        "Quels sont les avantages et les inconvénients de la commercialisation des idées et des émotions ?",
        "Existe-t-il des limites éthiques à la monétisation des valeurs humaines fondamentales ?",
      ],
      opinion: [
        "Quelle est votre position sur la marchandisation des émotions et des valeurs ?",
        "Pensez-vous que certaines idées devraient rester hors du champ commercial ? Pourquoi ?",
      ],
      ouverture: [
        "Comment voyez-vous l'avenir de l'économie des idées à l'ère de l'intelligence artificielle ?",
        "Quelles évolutions possibles dans la relation entre créativité, technologie et commerce ?",
      ],
      vocabulaire: [
        { mot: 'la marchandisation', def: "le fait de transformer quelque chose en produit commercial" },
        { mot: 'un concept abstrait', def: "une idée qui n'a pas de forme physique (liberté, amour, vérité)" },
        { mot: 'la propriété intellectuelle', def: "les droits sur les créations de l'esprit" },
        { mot: 'l\'éthique', def: "l'ensemble des principes moraux qui guident les comportements" },
        { mot: 'la monétisation', def: "le fait de tirer un profit financier de quelque chose" },
        { mot: 'immatériel(le)', def: "qui n'a pas de substance physique" },
        { mot: 'la manipulation', def: "l'action d'influencer quelqu'un de manière détournée" },
        { mot: 'l\'économie créative', def: "le secteur économique basé sur la créativité et l'innovation" },
      ],
      expressionOrale: "Préparez un exposé de 3 à 5 minutes. Analysez la symbolique de cette image, développez vos arguments sur la valeur des idées, nuancez et concluez.",
      exemple: [
        "Cette image fascinante représente un marché nocturne où des concepts abstraits — liberté, peur, succès, espoir, amour, vérité — sont présentés comme des marchandises sur des étals lumineux. Des visiteurs déambulent, observant ces « produits » immatériels.",
        "Tout d'abord, cette représentation artistique soulève une question fondamentale : dans notre société de consommation, les idées et les émotions sont-elles devenues des objets marchands ? La publicité, par exemple, vend rarement un produit ; elle vend un rêve, une émotion, un statut social.",
        "De plus, l'essor des réseaux sociaux a amplifié ce phénomène. Les créateurs de contenu monétisent des idées, des opinions et des émotions à travers des plateformes numériques, transformant l'intimité en spectacle rentable.",
        "En revanche, on pourrait argumenter que la valorisation des idées est au fondement même de l'économie créative. Les brevets, les droits d'auteur et la propriété intellectuelle protègent les créateurs et stimulent l'innovation.",
        "Cependant, il existe des limites éthiques à cette marchandisation. Lorsque des entreprises exploitent la peur, l'espoir ou l'amour pour manipuler les comportements d'achat, on franchit une frontière morale préoccupante.",
        "À mon avis, il est essentiel de distinguer la valorisation légitime de la créativité et de l'innovation de la manipulation émotionnelle à des fins purement commerciales. Les consommateurs doivent développer un esprit critique face aux messages publicitaires.",
        "Il faut également que les gouvernements renforcent la régulation de la publicité émotionnelle, notamment celle qui cible les jeunes et les personnes vulnérables.",
        "En conclusion, si la commercialisation des idées est inévitable dans une économie de marché, elle doit s'accompagner d'une réflexion éthique permanente. L'avenir réside dans un équilibre entre innovation, liberté créative et responsabilité sociale.",
      ],
    },
    {
      id: 'nature-technologie',
      title: "L'harmonie entre nature et technologie",
      objectif: "Développer un exposé structuré sur le développement durable et l'innovation technologique, argumenter et nuancer.",
      instructions: "Préparez un exposé oral de 3 à 5 minutes. Organisez votre réponse (introduction, développement, conclusion).",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/pugttx47_Harmony%20of%20nature%20and%20technology.png',
      analyse: [
        "Décrivez brièvement les éléments visuels de cette image et leur symbolique.",
        "Quel est le thème principal illustré par la coexistence de la nature et de la technologie ?",
        "Quels enjeux environnementaux et technologiques peut-on identifier ?",
      ],
      problematique: "Dans quelle mesure la technologie peut-elle être un outil au service de la préservation de l'environnement, ou constitue-t-elle une menace supplémentaire pour la nature ?",
      argumentation: [
        "Donnez au moins deux arguments montrant comment la technologie peut contribuer à la protection de l'environnement. Illustrez avec des exemples concrets (énergies renouvelables, agriculture de précision, etc.).",
        "Comment les innovations technologiques permettent-elles de réduire l'empreinte écologique des sociétés modernes ?",
        "Développez l'idée selon laquelle une transition écologique réussie nécessite une collaboration entre progrès scientifique et respect de la nature.",
      ],
      nuance: [
        "Quels sont les avantages et les inconvénients de la dépendance technologique dans la lutte contre le changement climatique ?",
        "Existe-t-il des critiques légitimes à l'égard du « solutionnisme technologique », c'est-à-dire la croyance que la technologie résoudra tous les problèmes environnementaux ?",
      ],
      opinion: [
        "Quelle est votre position sur le rôle de la technologie dans la transition écologique ?",
        "Pensez-vous que l'humanité peut atteindre un équilibre durable entre progrès et nature ? Pourquoi ?",
      ],
      ouverture: [
        "Comment voyez-vous l'avenir de la relation entre technologie et environnement dans 30 ans ?",
        "Quelles innovations futures pourraient transformer notre rapport à la nature ?",
      ],
      vocabulaire: [
        { mot: 'le développement durable', def: "un développement qui répond aux besoins du présent sans compromettre l'avenir" },
        { mot: "les énergies renouvelables", def: "les sources d'énergie inépuisables (solaire, éolienne, hydraulique)" },
        { mot: "l'empreinte écologique", def: "l'impact des activités humaines sur l'environnement" },
        { mot: 'la transition écologique', def: "le passage vers un modèle de société plus respectueux de l'environnement" },
        { mot: "la biodiversité", def: "la diversité des espèces vivantes dans un écosystème" },
        { mot: "le solutionnisme technologique", def: "la croyance que la technologie peut résoudre tous les problèmes" },
        { mot: "l'intelligence artificielle", def: "des systèmes informatiques capables d'imiter l'intelligence humaine" },
        { mot: "la coexistence", def: "le fait de vivre ensemble de manière harmonieuse" },
      ],
      expressionOrale: "Préparez un exposé de 3 à 5 minutes. Analysez la symbolique de l'image, argumentez sur le rôle de la technologie face aux défis environnementaux, nuancez et concluez.",
      exemple: [
        "Cette image spectaculaire représente un arbre monumental au centre, dont une moitié est naturelle — avec des feuilles vertes, une cascade et un cerf — tandis que l'autre moitié est parcourue de circuits numériques et de lumières artificielles. Autour, des panneaux solaires coexistent avec une forêt luxuriante. Cette allégorie illustre parfaitement le défi de notre époque : concilier progrès technologique et préservation de la nature.",
        "Tout d'abord, il est indéniable que la technologie offre des solutions concrètes aux problèmes environnementaux. Les énergies renouvelables — solaire, éolienne, hydraulique — permettent de réduire considérablement les émissions de gaz à effet de serre.",
        "De plus, l'intelligence artificielle et les capteurs connectés révolutionnent l'agriculture et la gestion des ressources naturelles, en optimisant la consommation d'eau et en réduisant l'utilisation de pesticides.",
        "En revanche, il serait imprudent de croire que la technologie seule peut résoudre la crise écologique. La fabrication des panneaux solaires et des batteries nécessite l'extraction de métaux rares, ce qui engendre des dommages environnementaux considérables.",
        "Cependant, le solutionnisme technologique présente un risque : celui de déresponsabiliser les citoyens et les gouvernements en leur faisant croire qu'une innovation future viendra effacer les conséquences de nos modes de vie actuels.",
        "À mon avis, la véritable solution réside dans une approche hybride : utiliser la technologie comme un outil complémentaire, tout en transformant nos comportements quotidiens et nos systèmes économiques.",
        "Il faut que les citoyens, les entreprises et les États collaborent pour créer un modèle de développement où la technologie est au service de la nature, et non l'inverse.",
        "En conclusion, l'harmonie entre nature et technologie n'est pas une utopie, mais elle exige une volonté politique forte, une éducation environnementale généralisée et une innovation responsable. L'avenir de notre planète dépend de notre capacité à trouver cet équilibre fragile.",
      ],
    },
    {
      id: 'champagne-production',
      title: 'Le champagne — Patrimoine gastronomique et enjeux économiques',
      objectif: "Développer un exposé structuré sur l'industrie du champagne, le patrimoine français et les défis de la mondialisation.",
      instructions: "Préparez un exposé oral de 3 à 5 minutes. Organisez votre réponse (introduction, développement, conclusion).",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/bqvuhb4m_Champagne%20production%20in%20the%20vineyards.png',
      analyse: [
        "Décrivez brièvement les différentes étapes de production visibles dans cette image.",
        "Quel est le thème principal lié à cette scène de production de champagne ?",
        "Quels enjeux économiques, culturels et environnementaux peut-on identifier ?",
      ],
      problematique: "Quels sont les défis auxquels l'industrie du champagne doit faire face pour préserver son patrimoine tout en s'adaptant aux exigences du marché mondial et du changement climatique ?",
      argumentation: [
        "Donnez au moins deux arguments montrant l'importance du champagne pour l'identité culturelle et économique de la France. Illustrez avec des exemples.",
        "Comment l'appellation d'origine contrôlée (AOC) protège-t-elle le patrimoine viticole et quels en sont les bénéfices pour les producteurs ?",
        "Développez l'idée selon laquelle la mondialisation représente à la fois une opportunité et un défi pour les produits de terroir comme le champagne.",
      ],
      nuance: [
        "Quels sont les avantages et les inconvénients de l'industrialisation de la production de champagne par rapport aux méthodes artisanales ?",
        "Existe-t-il des critiques légitimes concernant l'impact environnemental de la viticulture intensive ?",
      ],
      opinion: [
        "Quelle est votre position sur la protection des produits de terroir face à la mondialisation ?",
        "Pensez-vous que la tradition et la modernité peuvent coexister dans l'industrie viticole ? Pourquoi ?",
      ],
      ouverture: [
        "Comment voyez-vous l'avenir de l'industrie du champagne face au réchauffement climatique ?",
        "Quelles évolutions sont nécessaires pour garantir la pérennité de ce patrimoine ?",
      ],
      vocabulaire: [
        { mot: "l'AOC (Appellation d'Origine Contrôlée)", def: "un label garantissant l'origine et la qualité d'un produit" },
        { mot: 'le terroir', def: "l'ensemble des caractéristiques géographiques et climatiques d'une région de production" },
        { mot: 'la viticulture', def: "la culture de la vigne pour la production de vin" },
        { mot: 'un vignoble', def: "une plantation de vignes destinée à la production de vin" },
        { mot: 'le patrimoine gastronomique', def: "l'héritage culinaire et alimentaire d'un pays ou d'une région" },
        { mot: 'la fermentation', def: "le processus chimique qui transforme le jus de raisin en vin" },
        { mot: 'le savoir-faire', def: "l'ensemble des compétences et techniques transmises de génération en génération" },
        { mot: "la mondialisation", def: "l'interconnexion croissante des économies et des cultures à l'échelle mondiale" },
      ],
      expressionOrale: "Préparez un exposé de 3 à 5 minutes. Présentez l'industrie du champagne, développez les enjeux économiques et culturels, nuancez votre propos et concluez avec une réflexion sur l'avenir.",
      exemple: [
        "Cette image représente une maison de champagne en pleine activité, avec des cuves en acier inoxydable, des fûts de chêne et des bouteilles en cours de maturation. En arrière-plan, on aperçoit les vignobles vallonnés de la région champenoise. Cette scène illustre la rencontre entre tradition artisanale et production moderne.",
        "Tout d'abord, le champagne est bien plus qu'une boisson : c'est un symbole de l'excellence française et un pilier de l'économie régionale. Avec plus de 300 millions de bouteilles produites chaque année, l'industrie champenoise génère des milliards d'euros de chiffre d'affaires et fait vivre des milliers de familles.",
        "De plus, l'AOC Champagne protège rigoureusement ce patrimoine en imposant des règles strictes de production, depuis la culture du raisin jusqu'à la mise en bouteille. Ce label garantit au consommateur un produit authentique et de qualité.",
        "En revanche, l'industrialisation croissante de la production soulève des questions sur la préservation du savoir-faire artisanal. Les grandes maisons de champagne privilégient parfois le volume au détriment de la qualité, ce qui peut nuire à l'image d'excellence du produit.",
        "Cependant, le changement climatique représente un défi majeur pour la viticulture champenoise. L'augmentation des températures modifie les conditions de maturation du raisin et menace l'équilibre des cépages traditionnels.",
        "À mon avis, l'avenir du champagne repose sur la capacité de l'industrie à innover tout en préservant ses traditions. Il est essentiel d'adopter des pratiques viticoles durables et de réduire l'empreinte carbone de la production.",
        "Il faut également que les producteurs investissent dans la recherche pour adapter les cépages aux nouvelles conditions climatiques, sans compromettre la typicité du champagne.",
        "En conclusion, le champagne incarne l'art de vivre à la française et constitue un patrimoine inestimable. Sa pérennité dépendra de la volonté des acteurs de la filière à conjuguer respect des traditions, innovation technologique et responsabilité environnementale.",
      ],
    },
    {
      id: 'bretagne-maritime',
      title: 'Les métiers maritimes en Bretagne — Tradition et modernité',
      objectif: "Développer un exposé structuré sur la préservation des métiers traditionnels face à la modernisation et la mondialisation.",
      instructions: "Préparez un exposé oral de 3 à 5 minutes. Organisez votre réponse (introduction, développement, conclusion).",
      imageUrl: 'https://customer-assets.emergentagent.com/job_fe3d3eb6-1021-4631-985a-dfcd32e461f7/artifacts/84258bbv_Bretagne%20%28Brittany%29%20Maritime%20Products%20%26%20Salt%20Production.png',
      analyse: [
        "Décrivez brièvement la situation et le métier représentés dans cette image.",
        "Quel est le thème principal lié à cette scène de production artisanale en Bretagne ?",
        "Quels enjeux économiques, culturels et environnementaux peut-on identifier ?",
      ],
      problematique: "Dans quelle mesure les métiers traditionnels comme la production de sel marin en Bretagne peuvent-ils survivre face à l'industrialisation et à la concurrence mondiale ?",
      argumentation: [
        "Donnez au moins deux arguments en faveur de la préservation des métiers artisanaux traditionnels. Illustrez avec des exemples concrets.",
        "Comment le tourisme et les labels de qualité peuvent-ils contribuer à la valorisation des produits régionaux ?",
        "Développez l'idée selon laquelle les savoir-faire traditionnels représentent un patrimoine culturel immatériel qu'il est essentiel de protéger.",
      ],
      nuance: [
        "Quels sont les avantages et les inconvénients de la modernisation des métiers traditionnels ?",
        "Existe-t-il des limites économiques à la préservation de ces métiers face à la concurrence industrielle ?",
      ],
      opinion: [
        "Quelle est votre position sur la valeur des métiers artisanaux dans une économie mondialisée ?",
        "Pensez-vous que les consommateurs sont prêts à payer plus cher pour des produits artisanaux ? Pourquoi ?",
      ],
      ouverture: [
        "Comment voyez-vous l'avenir des métiers traditionnels dans les régions côtières françaises ?",
        "Quelles politiques pourraient favoriser la transmission de ces savoir-faire aux nouvelles générations ?",
      ],
      vocabulaire: [
        { mot: 'un paludier', def: "un artisan qui récolte le sel dans les marais salants" },
        { mot: 'les marais salants', def: "des bassins peu profonds où l'eau de mer s'évapore pour produire du sel" },
        { mot: 'le sel marin', def: "le sel obtenu par évaporation de l'eau de mer" },
        { mot: 'un savoir-faire ancestral', def: "des compétences transmises depuis de nombreuses générations" },
        { mot: "le patrimoine immatériel", def: "les traditions, connaissances et pratiques culturelles non physiques" },
        { mot: "la concurrence", def: "la rivalité entre producteurs sur un même marché" },
        { mot: "un label de qualité", def: "une certification garantissant l'excellence d'un produit" },
        { mot: "la pérennité", def: "le caractère de ce qui dure dans le temps" },
      ],
      expressionOrale: "Préparez un exposé de 3 à 5 minutes. Présentez le métier de paludier, analysez les défis des métiers traditionnels, nuancez et concluez avec une réflexion sur l'avenir.",
      exemple: [
        "Cette image représente un paludier en Bretagne, en train de récolter le sel marin dans des marais salants, avec l'océan Atlantique et des maisons de pierre typiques en arrière-plan. Cette scène illustre un savoir-faire ancestral qui perdure depuis des siècles sur les côtes bretonnes.",
        "Tout d'abord, la production artisanale de sel marin est un exemple remarquable de patrimoine vivant. Le métier de paludier, transmis de génération en génération, est intimement lié à l'identité culturelle de la Bretagne et contribue à l'attractivité touristique de la région.",
        "De plus, les produits artisanaux bretons — sel de Guérande, beurre salé, poissons fumés — bénéficient de labels de qualité qui leur confèrent une valeur ajoutée sur le marché national et international, permettant aux producteurs de vivre dignement de leur métier.",
        "En revanche, ces métiers traditionnels font face à des défis considérables. La concurrence du sel industriel, produit en masse à moindre coût, menace la viabilité économique des marais salants artisanaux.",
        "Cependant, on peut se demander si la modernisation partielle de ces métiers ne risque pas de dénaturer leur authenticité. L'équilibre entre efficacité économique et préservation des techniques ancestrales est délicat à trouver.",
        "À mon avis, la survie des métiers traditionnels dépend avant tout de la sensibilisation des consommateurs. Lorsque les gens comprennent la valeur d'un produit artisanal — son histoire, son goût, son impact environnemental positif — ils sont souvent disposés à payer un prix juste.",
        "Il faut que les pouvoirs publics soutiennent ces filières par des aides financières, des formations et des programmes de transmission intergénérationnelle. L'inscription des savoir-faire au patrimoine culturel immatériel de l'UNESCO peut également renforcer leur visibilité.",
        "En conclusion, les métiers maritimes traditionnels de Bretagne ne sont pas de simples reliques du passé : ils incarnent un modèle de production durable et respectueux de l'environnement. Leur avenir repose sur notre capacité collective à valoriser l'authenticité dans un monde de plus en plus standardisé.",
      ],
    },
  ],
};

const LEVELS = ['A1', 'A2', 'B1', 'B2'];

function RecordButton({ testId }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch {
      alert('Microphone non disponible. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!isRecording ? (
        <button
          onClick={startRecording}
          className="flex items-center gap-1.5 text-xs text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg px-3 py-1.5 hover:bg-[#D4AF37]/5 transition-colors"
          data-testid={testId}>
          <Mic className="w-3 h-3" /> Enregistrer votre réponse
        </button>
      ) : (
        <button
          onClick={stopRecording}
          className="flex items-center gap-1.5 text-xs text-red-500 border border-red-300 rounded-lg px-3 py-1.5 bg-red-50 animate-pulse"
          data-testid={`${testId}-stop`}>
          <Square className="w-3 h-3" /> Arrêter
        </button>
      )}
      {audioUrl && (
        <audio controls src={audioUrl} className="h-8 max-w-[160px]" data-testid={`${testId}-playback`} />
      )}
    </div>
  );
}

function ActivityView({ activity, onBack }) {
  const [showExemple, setShowExemple] = useState(false);
  const [vocabOpen, setVocabOpen] = useState(false);

  return (
    <div className="space-y-5" data-testid={`activity-${activity.id}`}>
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-[#999] hover:text-[#D4AF37] transition-colors"
        data-testid="back-to-list">
        <ArrowRight className="w-3 h-3 rotate-180" /> Toutes les activités
      </button>

      {/* Title */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="activity-title">
          {activity.title}
        </h2>
        <p className="text-xs text-[#6B4E37] mt-1 italic">{activity.objectif}</p>
      </div>

      {/* Image */}
      <div className="rounded-xl overflow-hidden border border-[#E8E4DC] shadow-sm" data-testid="activity-image">
        <img src={activity.imageUrl} alt={activity.title} className="w-full h-auto" loading="lazy" />
      </div>

      {/* Instructions */}
      <div className="bg-[#1A2F4A] rounded-xl px-5 py-4" data-testid="activity-instructions">
        <p className="text-xs text-white/70 uppercase tracking-wider font-bold mb-1">Instructions</p>
        <p className="text-sm text-white/90 leading-relaxed">{activity.instructions}</p>
      </div>

      {/* Observation (A1/A2) */}
      {activity.observation && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="observation-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Observation</p>
          {activity.observation.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
        </div>
      )}

      {/* Analyse (B1+) */}
      {activity.analyse && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="analyse-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Analyse</p>
          {activity.analyse.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-analyse" />
        </div>
      )}

      {/* Problématique (B2) */}
      {activity.problematique && (
        <div className="bg-[#1A2F4A] rounded-xl p-5 space-y-3" data-testid="problematique-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Problématique</p>
          <p className="text-sm text-white/90 leading-relaxed italic">{activity.problematique}</p>
          <RecordButton testId="record-problematique" />
        </div>
      )}

      {/* Argumentation (B2) */}
      {activity.argumentation && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="argumentation-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Argumentation</p>
          {activity.argumentation.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-argumentation" />
        </div>
      )}

      {/* Nuance / Discussion (B2) */}
      {activity.nuance && (
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="nuance-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Nuance / Discussion</p>
          {activity.nuance.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-nuance" />
        </div>
      )}

      {/* Interprétation (B1+) */}
      {activity.interpretation && (
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="interpretation-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Interprétation</p>
          {activity.interpretation.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-interpretation" />
        </div>
      )}

      {/* Opinion (B1+) */}
      {activity.opinion && (
        <div className="bg-white border-2 border-[#D4AF37]/20 rounded-xl p-5 space-y-3" data-testid="opinion-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Opinion</p>
          {activity.opinion.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-opinion" />
        </div>
      )}

      {/* Ouverture (B2) */}
      {activity.ouverture && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="ouverture-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Ouverture</p>
          {activity.ouverture.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-ouverture" />
        </div>
      )}

      {/* Solution (B1+) */}
      {activity.solution && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="solution-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Solution</p>
          {activity.solution.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-solution" />
        </div>
      )}

      {/* Comparaison (B1+) */}
      {activity.comparaison && (
        <div className="bg-[#FAF7F0] border-2 border-[#D4AF37]/20 rounded-xl p-5 space-y-3" data-testid="comparaison-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Comparaison</p>
          <p className="text-sm text-[#1A2F4A] leading-relaxed">{activity.comparaison}</p>
          <RecordButton testId="record-comparaison" />
        </div>
      )}

      {/* Actions (A2+) */}
      {activity.actions && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="actions-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Actions</p>
          {activity.actions.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-actions" />
        </div>
      )}

      {/* Situation (A2+) */}
      {activity.situation && (
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="situation-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Situation</p>
          <p className="text-sm text-[#1A2F4A]">{activity.situation}</p>
          <RecordButton testId="record-situation" />
        </div>
      )}

      {/* Pourquoi (A2+) */}
      {activity.pourquoi && (
        <div className="bg-white border-2 border-[#D4AF37]/20 rounded-xl p-5 space-y-3" data-testid="pourquoi-section">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Pourquoi ?</p>
          <p className="text-sm text-[#1A2F4A] italic">{activity.pourquoi}</p>
          <RecordButton testId="record-pourquoi" />
        </div>
      )}

      {/* Expression personnelle (A2+) */}
      {activity.expressionPersonnelle && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid="expression-perso-section">
          <p className="text-xs text-[#1A2F4A] uppercase tracking-wider font-bold">Expression personnelle</p>
          {activity.expressionPersonnelle.map((q, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[#1A2F4A] font-bold text-sm mt-0.5">{i + 1}.</span>
              <p className="text-sm text-[#1A2F4A]">{q}</p>
            </div>
          ))}
          <RecordButton testId="record-expression-perso" />
        </div>
      )}

      {/* Vocabulaire */}
      <button
        onClick={() => setVocabOpen(!vocabOpen)}
        className="w-full flex items-center justify-between bg-[#1A2F4A] rounded-xl px-5 py-3 text-sm font-semibold text-white hover:bg-[#243a58] transition-colors"
        data-testid="vocab-toggle">
        <span>Vocabulaire clé</span>
        {vocabOpen ? <ChevronUp className="w-4 h-4 text-[#D4AF37]" /> : <ChevronDown className="w-4 h-4 text-[#D4AF37]" />}
      </button>
      {vocabOpen && (
        <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden" data-testid="vocab-content">
          {activity.vocabulaire.map((v, i) => (
            <div key={i} className={`flex items-center gap-3 px-5 py-2.5 ${i > 0 ? 'border-t border-[#F3F4F6]' : ''}`}>
              <p className="text-sm font-bold text-[#1A2F4A] min-w-[120px]">{v.mot}</p>
              <p className="text-xs text-[#999]">&rarr;</p>
              <p className="text-sm text-[#6B4E37]">{v.def}</p>
            </div>
          ))}
        </div>
      )}

      {/* Expression guidée (A1 only) */}
      {activity.expressionGuidee && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-[#D4AF37]/30" />
            <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider whitespace-nowrap">Expression guidée</p>
            <div className="h-px flex-1 bg-[#D4AF37]/30" />
          </div>
          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5" data-testid="expression-guidee">
            <p className="text-xs text-[#999] mb-3">Dites ces phrases à voix haute :</p>
            {activity.expressionGuidee.map((phrase, i) => (
              <p key={i} className="text-sm text-[#1A2F4A] leading-relaxed mb-1.5">
                <span className="text-[#D4AF37] font-bold mr-1">{i + 1}.</span> {phrase}
              </p>
            ))}
            <div className="mt-4">
              <RecordButton testId="record-guidee" />
            </div>
          </div>
        </div>
      )}

      {/* Expression orale */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#1A2F4A]/15" />
          <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider whitespace-nowrap">Expression orale</p>
          <div className="h-px flex-1 bg-[#1A2F4A]/15" />
        </div>
        <div className="bg-white border-2 border-[#D4AF37]/30 rounded-xl p-5" data-testid="expression-orale">
          <p className="text-sm text-[#1A2F4A] leading-relaxed mb-4">{activity.expressionOrale}</p>
          <RecordButton testId="record-orale" />
        </div>
      </div>

      {/* Exemple — hidden by default */}
      <button
        onClick={() => setShowExemple(!showExemple)}
        className="w-full flex items-center justify-center gap-2 bg-[#FAF7F0] border border-[#E8E4DC] text-[#1A2F4A] py-3 rounded-xl text-sm font-bold hover:border-[#D4AF37] transition-colors"
        data-testid="exemple-toggle">
        <Eye className="w-4 h-4" /> {showExemple ? 'Cacher l\'exemple' : 'Voir un exemple'}
      </button>
      {showExemple && (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5" data-testid="exemple-content">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-2">Exemple de réponse</p>
          {activity.exemple.map((line, i) => (
            <p key={i} className="text-sm text-[#1A2F4A] leading-relaxed mb-1 italic">{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ApprendreParImage() {
  const [activeLevel, setActiveLevel] = useState('A1');
  const [activeActivity, setActiveActivity] = useState(null);

  const activities = ACTIVITIES[activeLevel] || [];

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Hero */}
      <section className="pt-16 pb-8">
        <div className="max-w-[520px] mx-auto px-8 text-center">
          <Link to="/speaking-lab" className="inline-flex items-center gap-1 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-to-lab">
            <ArrowRight className="w-3 h-3 rotate-180" /> Speaking Lab
          </Link>
          <h1
            className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="page-title">
            Apprendre par l'image
          </h1>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#D4AF37]/50" />
            <Image className="w-4 h-4 text-[#D4AF37]/60" />
            <div className="h-px w-8 bg-[#D4AF37]/50" />
          </div>
          <p className="text-xs text-[#6B4E37] italic">
            Observez, décrivez et parlez à partir d'images du quotidien.
          </p>
        </div>
      </section>

      {/* Level Tabs */}
      <section className="pb-4">
        <div className="max-w-[520px] mx-auto px-8 flex gap-2 justify-center" data-testid="level-tabs">
          {LEVELS.map(lvl => {
            const hasContent = (ACTIVITIES[lvl] || []).length > 0;
            return (
              <button
                key={lvl}
                onClick={() => { if (hasContent) { setActiveLevel(lvl); setActiveActivity(null); } }}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  activeLevel === lvl
                    ? 'bg-[#1A2F4A] text-white'
                    : hasContent
                      ? 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                      : 'bg-[#F3F4F6] text-[#ccc] cursor-not-allowed'
                }`}
                data-testid={`level-btn-${lvl}`}>
                {lvl} {!hasContent && <span className="text-[9px] ml-0.5 opacity-60">soon</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-[520px] mx-auto px-8">
          {activeActivity ? (
            <ActivityView activity={activeActivity} onBack={() => setActiveActivity(null)} />
          ) : (
            <div className="space-y-3" data-testid="activity-list">
              {activities.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-4xl mb-3">🖼️</p>
                  <p className="text-sm text-[#999]">Contenu {activeLevel} bientôt disponible</p>
                </div>
              ) : (
                activities.map((act, i) => (
                  <button
                    key={act.id}
                    onClick={() => setActiveActivity(act)}
                    className="w-full bg-white border border-[#E8E4DC] rounded-xl overflow-hidden hover:border-[#D4AF37] hover:shadow-sm transition-all text-left group"
                    data-testid={`activity-btn-${act.id}`}>
                    <div className="h-32 overflow-hidden">
                      <img src={act.imageUrl} alt={act.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                    <div className="p-4 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1A2F4A] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {act.title}
                        </p>
                        <p className="text-xs text-[#999] mt-0.5">{(act.observation || act.analyse || []).length} questions + expression</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#E8E4DC] group-hover:text-[#D4AF37] transition-colors" />
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
